/**
 * TransparencyPage — public cryptographic witness surface.
 *
 * Fetches `/api/canary` and `/api/canary/matrix-mirror` on mount
 * and renders both:
 *
 *   • The authoritative warrant-canary signature block (same data
 *     the Dashboard's canary widget consumes).
 *   • The list of Matrix rooms + event_ids GS CLAIMS to have
 *     posted the current canary to, each with a one-click
 *     `matrix.to` deep-link so a visitor can open the event
 *     directly in Element (or any Matrix client) and diff the
 *     `org.govshield.canary.signature_b64` field against the
 *     authoritative value rendered above.
 *
 * If the Matrix bridge is not configured on this deployment, the
 * mirror section renders a transparent "bridge disabled" note
 * instead of an error — the primary canary is still a full witness
 * on its own.
 *
 * Route: /transparency
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck,
  Radio,
  Mic,
  ExternalLink,
  Copy,
  AlertTriangle,
  Loader2,
  Lock,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 4 hours. Matches the canary's expected re-sign cadence, so a
// tab left open overnight will auto-pick-up the fresh signature
// without any manual reload. Not tied to the 30s ticker that
// re-renders the "last refreshed" chip.
const AUTO_REFRESH_MS = 4 * 60 * 60 * 1000;

const _copy = (text, label) => {
  try {
    navigator.clipboard?.writeText(text);
    toast.success(`Copied ${label}`);
  } catch {
    toast.error('Clipboard blocked');
  }
};

/**
 * "Xm ago" / "Xh ago" / "just now" — used by the live-refresh
 * chip. Pure function of the caller's local clock so it's safe
 * to call on every render.
 */
const formatAgo = (timestamp) => {
  if (!timestamp) return '—';
  const d = Date.now() - timestamp;
  if (d < 15_000) return 'just now';
  if (d < 60_000) return `${Math.round(d / 1000)}s ago`;
  if (d < 3600_000) return `${Math.round(d / 60_000)}m ago`;
  if (d < 86400_000) return `${Math.round(d / 3600_000)}h ago`;
  return `${Math.round(d / 86400_000)}d ago`;
};

/**
 * Build a matrix.to deep-link for a room+event pair. We try the
 * homeserver's canonical domain first; `matrix.to` handles the
 * cross-client dispatch so Element / FluffyChat / Hydrogen users
 * all land on the right event.
 *
 * Format (per matrix.to docs):
 *   https://matrix.to/#/<room_id_or_alias>/<event_id>
 *
 * Both parameters must be url-encoded. The leading `!` in room_ids
 * and `$` in event_ids matter for matrix.to's parser so we don't
 * strip them.
 */
const matrixToLink = (roomId, eventId) => {
  if (!roomId || !eventId) return null;
  return `https://matrix.to/#/${encodeURIComponent(roomId)}/${encodeURIComponent(eventId)}`;
};

const SignatureBlock = ({ canary }) => {
  if (!canary) return null;
  const fp = canary.public_key_fingerprint_short || '';
  const sig = canary.signature_b64 || '';
  const age = canary.age_hours;
  const stale = canary.is_stale;
  return (
    <div
      className="rounded-xl border border-border/60 bg-card/40 p-6 space-y-4"
      data-testid="transparency-canary-block"
    >
      <div className="flex items-center gap-2">
        <ShieldCheck className={`w-5 h-5 ${stale ? 'text-rose-400' : 'text-emerald-400'}`} />
        <h2 className="text-lg font-semibold text-slate-100">
          Authoritative warrant canary
        </h2>
        {stale && (
          <span
            className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30"
            data-testid="transparency-canary-stale"
          >
            <AlertTriangle className="w-3 h-3" /> Stale
          </span>
        )}
      </div>
      <p className="text-sm text-slate-400 leading-relaxed">
        The fingerprint below is ed25519. Pin it out-of-band — in your own
        notes, on a sticker, anywhere — and the server can never silently
        swap keys on you.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Signed at</div>
          <div className="font-mono text-slate-200" data-testid="transparency-canary-signed-at">
            {canary.signed_at || '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Age</div>
          <div className="font-mono text-slate-200">
            {age == null ? '—' : `${age.toFixed(1)} hours`}
          </div>
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
          Public-key fingerprint (first 32 hex chars)
        </div>
        <button
          type="button"
          onClick={() => _copy(canary.public_key_hex || '', 'public key')}
          className="w-full text-left font-mono text-xs text-emerald-200 bg-emerald-500/5 border border-emerald-500/20 rounded-md px-3 py-2 hover:bg-emerald-500/10 transition"
          data-testid="transparency-canary-fingerprint"
        >
          {fp || '—'}
        </button>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-2">
          Signature
          <button
            type="button"
            className="text-slate-400 hover:text-slate-200"
            onClick={() => _copy(sig, 'signature')}
            aria-label="Copy signature"
          >
            <Copy className="w-3 h-3" />
          </button>
        </div>
        <div
          className="font-mono text-[10px] text-slate-300 break-all bg-background/60 border border-border/60 rounded-md px-3 py-2 max-h-24 overflow-auto"
          data-testid="transparency-canary-signature"
        >
          {sig || '—'}
        </div>
      </div>
    </div>
  );
};

const MatrixMirrorBlock = ({ mirror }) => {
  const lb = mirror?.last_broadcast;
  const enabled = mirror?.enabled;
  const events = lb?.events || [];
  return (
    <div
      className="rounded-xl border border-violet-500/25 bg-violet-950/10 p-6 space-y-4"
      data-testid="transparency-matrix-block"
    >
      <div className="flex items-center gap-2">
        <Radio className="w-5 h-5 text-violet-300" />
        <h2 className="text-lg font-semibold text-slate-100">
          Federated mirror · Matrix
        </h2>
      </div>
      {!enabled || !lb ? (
        <p
          className="text-sm text-slate-400 leading-relaxed"
          data-testid="transparency-matrix-disabled"
        >
          The Matrix mirror bridge is not configured on this deployment. The
          canary above is still a complete witness on its own — mirror
          activation turns the signal into a multi-surface cross-check.
          Operators: set <code className="text-xs text-violet-300">MATRIX_HOMESERVER_URL</code>,{' '}
          <code className="text-xs text-violet-300">MATRIX_ACCESS_TOKEN</code>, and{' '}
          <code className="text-xs text-violet-300">MATRIX_BROADCAST_ROOMS</code>.
        </p>
      ) : (
        <>
          <p className="text-sm text-slate-400 leading-relaxed">
            We claim to have published the current canary to{' '}
            <span className="text-violet-200 font-mono">{lb.ok_count}</span> of{' '}
            <span className="text-violet-200 font-mono">{lb.total_rooms}</span> Matrix rooms on{' '}
            <span className="text-violet-200 font-mono">{lb.homeserver}</span>. Open any event
            below and diff its <code className="text-xs text-violet-300">org.govshield.canary.signature_b64</code>{' '}
            against the signature above.
          </p>
          <div className="space-y-2" data-testid="transparency-matrix-events">
            {events.length === 0 ? (
              <p className="text-sm text-slate-500">No events recorded.</p>
            ) : (
              events.map((ev) => {
                const link = matrixToLink(ev.room_id, ev.event_id);
                const ok = ev.ok;
                return (
                  <div
                    key={ev.txn_id || `${ev.room_id}-${ev.event_id}`}
                    className="flex items-center justify-between gap-2 p-3 rounded-md bg-background/40 border border-border/60"
                    data-testid="transparency-matrix-event-row"
                    data-ok={ok ? 'true' : 'false'}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">
                        Room
                      </div>
                      <div className="font-mono text-xs text-slate-200 truncate">
                        {ev.room_id}
                      </div>
                      <div className="font-mono text-[10px] text-slate-500 truncate mt-0.5">
                        {ev.event_id || '(no event_id — send failed)'}
                      </div>
                    </div>
                    {ok && link ? (
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 text-xs px-3 py-1.5 rounded-md bg-violet-500/15 text-violet-200 border border-violet-500/40 hover:bg-violet-500/25 transition inline-flex items-center gap-1"
                        data-testid="transparency-matrix-event-open"
                      >
                        Open <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span
                        className="shrink-0 text-xs px-3 py-1.5 rounded-md bg-rose-500/10 text-rose-300 border border-rose-500/30"
                        data-testid="transparency-matrix-event-failed"
                      >
                        Send failed
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
      {lb?.created_at && (
        <div className="text-[10px] text-slate-500 pt-2 border-t border-violet-500/10">
          Mirrored at {lb.created_at}
        </div>
      )}
    </div>
  );
};

/**
 * Voice-call stack attestation block.
 *
 * Renders the architectural posture of the LiveKit SFU integration:
 * egress disabled (no recording), ingress disabled (no external
 * stream ingest), E2EE required (SFrame + our Sealed-Sender key
 * channel), max-participant cap, cipher. Same cryptographic-witness
 * pattern as the canary + Matrix blocks.
 */
const VoiceStackBlock = ({ stack }) => {
  if (!stack) return null;
  return (
    <div
      className="rounded-xl border border-emerald-500/25 bg-emerald-950/10 p-6 space-y-4"
      data-testid="transparency-voice-stack-block"
    >
      <div className="flex items-center gap-2">
        <Mic className="w-5 h-5 text-emerald-300" />
        <h2 className="text-lg font-semibold text-slate-100">
          Voice-call stack · LiveKit SFU
        </h2>
        <span
          className={`ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
            stack.enabled
              ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
              : 'bg-slate-500/10 text-slate-400 border-slate-500/30'
          }`}
          data-testid="transparency-voice-stack-enabled"
        >
          {stack.enabled ? 'Active' : 'Not configured'}
        </span>
      </div>
      <p className="text-sm text-slate-400 leading-relaxed">
        {stack.note}
      </p>
      <div
        className="grid grid-cols-2 sm:grid-cols-4 gap-2"
        data-testid="transparency-voice-stack-attestations"
      >
        {[
          ['Egress', stack.egress_disabled ? 'disabled' : 'enabled', stack.egress_disabled],
          ['Ingress', stack.ingress_disabled ? 'disabled' : 'enabled', stack.ingress_disabled],
          ['E2EE', stack.e2ee_required ? 'required' : 'optional', stack.e2ee_required],
          ['Cipher', stack.e2ee_cipher || '—', true],
        ].map(([label, value, good]) => (
          <div
            key={label}
            className="rounded-md bg-background/40 border border-border/60 p-2.5"
            data-testid={`transparency-voice-${label.toLowerCase()}`}
          >
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div
              className={`font-mono text-xs mt-1 ${
                good ? 'text-emerald-300' : 'text-amber-300'
              }`}
            >
              {value}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3 pt-3 border-t border-emerald-500/10 text-[11px] text-slate-500">
        <span>Max {stack.max_participants_per_room} participants per room</span>
        {stack.enabled && stack.url && (
          <span className="font-mono text-emerald-300/80 truncate max-w-[60%]">
            {stack.url}
          </span>
        )}
      </div>

      {/* Live witness — actually pings the SFU and proves the
          backend has working credentials. Only rendered when
          the static attestation already says enabled, otherwise
          the live tile would just echo "not configured" twice. */}
      {stack.enabled && <LiveWitnessTile />}
    </div>
  );
};


/**
 * LiveWitnessTile — companion to the static VoiceStackBlock that
 * makes the SFU's existence verifiable rather than just claimed.
 *
 * On mount: GET /api/voice/live-witness → renders an aggregate
 * proof-of-life (sfu_responsive · roundtrip · active_room_count
 * · checked_at). Refreshes itself every 60s — the backend caches
 * for 30s anyway, so anything tighter is redundant load.
 *
 * Failure modes:
 *   - HTTP error → render a neutral "Probe unavailable" line so
 *     the rest of the transparency page keeps rendering.
 *   - sfu_responsive=false → amber tint, surface the `reason`.
 *   - sfu_responsive=true → green tint, render the metrics.
 *
 * Privacy: no room names, no per-room participant counts. The
 * aggregate `active_room_count` is the only quantitative leak,
 * which is no worse than what an operator could publish anyway.
 */
const LiveWitnessTile = () => {
  const [witness, setWitness] = useState(null);
  const [tick, setTick] = useState(0);
  // `probing` flips while a forced probe is in flight so the
  // refresh icon can spin + the button can be disabled to
  // dodge double-clicks. Auto-refreshes don't toggle this —
  // the user shouldn't see UI churn from invisible polling.
  const [probing, setProbing] = useState(false);

  const fetchWitness = async (force = false) => {
    if (force) setProbing(true);
    try {
      const url = `${API}/voice/live-witness${force ? '?force=true' : ''}`;
      const { data } = await axios.get(url);
      setWitness(data);
    } catch {
      setWitness({ enabled: true, sfu_responsive: false, reason: 'Probe failed' });
    } finally {
      if (force) setProbing(false);
    }
  };

  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        const { data } = await axios.get(`${API}/voice/live-witness`);
        if (alive) setWitness(data);
      } catch {
        if (alive) setWitness({ enabled: true, sfu_responsive: false, reason: 'Probe failed' });
      }
    };
    refresh();
    const refreshId = setInterval(refresh, 60_000);
    const tickId = setInterval(() => setTick((n) => n + 1), 5_000);
    return () => {
      alive = false;
      clearInterval(refreshId);
      clearInterval(tickId);
    };
  }, []);

  if (!witness) {
    return (
      <div
        className="mt-4 rounded-lg border border-emerald-500/20 bg-black/30 p-3 text-[11px] text-slate-500"
        data-testid="transparency-live-witness-loading"
      >
        Probing SFU…
      </div>
    );
  }

  const responsive = witness.sfu_responsive === true;
  const checkedMs = witness.checked_at ? Date.parse(witness.checked_at) : null;
  const ageS = checkedMs ? Math.max(0, Math.round((Date.now() - checkedMs) / 1000)) : null;

  return (
    <div
      className={`mt-4 rounded-lg border p-3 ${
        responsive
          ? 'border-emerald-500/30 bg-emerald-950/20'
          : 'border-amber-500/30 bg-amber-950/15'
      }`}
      data-testid="transparency-live-witness"
      data-responsive={String(responsive)}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className={`inline-block w-2 h-2 rounded-full ${
            responsive ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'
          }`}
          aria-hidden="true"
        />
        <span className="text-[11px] uppercase tracking-[0.18em] text-slate-300 font-medium">
          Live witness
        </span>
        <span
          className="ml-auto flex items-center gap-2 text-[10px] text-slate-500"
          data-testid="transparency-live-witness-age"
        >
          {/* Tick state forces this to re-render every 5s without
              re-fetching, so the "12s ago" stays honest while the
              underlying probe remains 60s-cached. */}
          {tick >= 0 && ageS !== null
            ? ageS < 60
              ? `verified ${ageS}s ago`
              : `verified ${Math.floor(ageS / 60)}m ago`
            : 'verified just now'}
          <button
            type="button"
            onClick={() => fetchWitness(true)}
            disabled={probing}
            className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded text-slate-400 hover:text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Force a fresh probe of the SFU"
            data-testid="transparency-live-witness-refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 ${probing ? 'animate-spin' : ''}`} />
          </button>
        </span>
      </div>
      {responsive ? (
        <div
          className="grid grid-cols-2 gap-2 text-[11px]"
          data-testid="transparency-live-witness-metrics"
        >
          <div className="rounded-md bg-black/30 border border-emerald-500/20 p-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Roundtrip</div>
            <div className="font-mono text-emerald-300 mt-0.5">
              {witness.roundtrip_ms} ms
            </div>
          </div>
          <div className="rounded-md bg-black/30 border border-emerald-500/20 p-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Active rooms</div>
            <div className="font-mono text-emerald-300 mt-0.5">
              {witness.active_room_count}
            </div>
          </div>
        </div>
      ) : (
        <p className="text-[11px] text-amber-200/90 leading-relaxed">
          {witness.reason || 'SFU did not respond.'}
        </p>
      )}
      <p className="mt-2 text-[10px] text-slate-500 leading-relaxed">
        This tile makes a real admin-API call to the configured SFU.
        It proves the backend has working credentials — not just that
        someone wrote a wss:// URL into the config. No room names or
        participant identities are exposed.
      </p>
    </div>
  );
};


export const TransparencyPage = () => {
  const [canary, setCanary] = useState(null);
  const [mirror, setMirror] = useState(null);
  const [stack, setStack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Last successful (or attempted) refresh time for the "live
  // dashboard" chip. Initialised to null so the first render
  // doesn't claim a refresh happened before one did.
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  // Used to re-render the "Xm ago" chip every 30s without
  // re-fetching — the actual API calls run on a much longer
  // cadence (AUTO_REFRESH_MS, below).
  const [_tick, setTick] = useState(0);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch both in parallel — neither requires auth and they're
      // independent. Axios surfaces network / 5xx errors as thrown
      // exceptions; HTTP 200 bodies land in `data`.
      const [canaryRes, mirrorRes, stackRes] = await Promise.all([
        axios.get(`${API}/canary`),
        axios.get(`${API}/canary/matrix-mirror`),
        axios.get(`${API}/voice/stack-attestation`),
      ]);
      setCanary(canaryRes.data);
      setMirror(mirrorRes.data);
      setStack(stackRes.data);
    } catch (e) {
      setError(e?.message || 'Failed to fetch transparency data');
    } finally {
      setLoading(false);
      setLastRefreshedAt(Date.now());
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // Live-dashboard auto-refresh. 4 hours matches the canary's
  // expected re-sign cadence closely enough that a reviewer
  // leaving the tab open overnight will see the new signature
  // land on its own. Separately, a 30s ticker re-renders the
  // "Xm ago" chip so a glance tells the reviewer the page is
  // live, not a stale snapshot.
  useEffect(() => {
    const refreshId = setInterval(reload, AUTO_REFRESH_MS);
    const tickId = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => {
      clearInterval(refreshId);
      clearInterval(tickId);
    };
  }, [reload]);

  return (
    <div
      className="min-h-screen bg-background text-slate-100"
      data-testid="transparency-page"
    >
      <header className="border-b border-border/40 bg-background/80 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="text-sm text-slate-400 hover:text-slate-200">
            ← Government Shield
          </Link>
          <div className="flex items-center gap-3">
            {lastRefreshedAt && (
              <button
                type="button"
                onClick={reload}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border border-border/60 bg-background/60 text-slate-400 hover:text-slate-200 hover:border-border transition"
                title="Click to refresh now"
                data-testid="transparency-last-refreshed"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Live · {formatAgo(lastRefreshedAt)}
              </button>
            )}
            <div className="text-[10px] uppercase tracking-[3px] text-primary/80 font-semibold">
              Transparency
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        <section className="space-y-3">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
            Cryptographic witness.
          </h1>
          <p className="text-slate-400 leading-relaxed max-w-2xl">
            Everything on this page is a live fetch from this deployment's public
            API — <span className="font-mono text-emerald-300">/api/canary</span>{' '}
            and <span className="font-mono text-violet-300">/api/canary/matrix-mirror</span>.
            Both endpoints are unauthenticated by design. If you can see this
            page, you can independently verify the canary's liveness AND
            (where the Matrix bridge is active) cross-check it against one or
            more federated homeservers in under a minute.
          </p>
        </section>

        {loading && (
          <div
            className="flex items-center gap-2 text-slate-400"
            data-testid="transparency-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin" /> Fetching witness data…
          </div>
        )}
        {error && (
          <div
            className="rounded-md border border-rose-500/40 bg-rose-950/20 px-4 py-3 text-sm text-rose-200"
            data-testid="transparency-error"
          >
            {error}
            <Button
              variant="outline"
              size="sm"
              onClick={reload}
              className="ml-3 border-rose-500/40 text-rose-200 hover:bg-rose-500/10"
              data-testid="transparency-retry"
            >
              Retry
            </Button>
          </div>
        )}

        {!loading && !error && (
          <>
            <SignatureBlock canary={canary} />
            <MatrixMirrorBlock mirror={mirror} />
            <VoiceStackBlock stack={stack} />
            <div className="pt-4 text-xs text-slate-500 leading-relaxed">
              Three-step third-party verification: (1) GET{' '}
              <code className="text-emerald-300">/api/canary</code> to read the
              authoritative signature. (2) For each event in the Matrix block
              above, fetch the corresponding event from the listed homeserver
              via{' '}
              <code className="text-violet-300">
                /_matrix/client/v3/rooms/&#123;room&#125;/event/&#123;event_id&#125;
              </code>
              . (3) Diff the{' '}
              <code className="text-violet-300">
                org.govshield.canary.signature_b64
              </code>{' '}
              field against the signature from step 1. A matching value on both
              surfaces is the witness — a forger would need simultaneous
              compromise of this server, the remote homeserver, and the
              ed25519 private key.
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default TransparencyPage;
